import { useCallback, useContext, useEffect } from 'react'
import { useIntl } from 'react-intl'
import ModalContext from 'contexts/ModalContext'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { patchCartItemStatus } from 'tripPlanner/api/cartStatus'
import type { ManualItineraryDeleteRequest } from 'tripPlanner/api/types'
import type { CartPanelFlightItem, CartPanelItem } from './useCartPanelItems'
import ConfirmDeleteCartItemModal from '../components/ConfirmDeleteCartItemModal'
import { useTripChatFlightCartActions } from './useTripChatFlightCartActions'
import useViewBookedShortlistedFlight from '../components/ShortlistedFlightsSection/useViewBookedShortlistedFlight'
import { useTripChatCheckoutActions } from './useTripChatCheckoutActions'
import { rememberTripChatCheckoutReturnView } from './useRightPanelState'

interface UseCartPanelActionsArgs {
  chatId: number | undefined
  multiCartId: string | undefined
  items: Array<CartPanelItem>
  isStreaming: boolean
  isLoggedIn: boolean
  sendMessage: (message: string) => Promise<void>
  deleteManualItineraryTarget: (target: ManualItineraryDeleteRequest) => Promise<void>
  setOptimisticExclusions: React.Dispatch<React.SetStateAction<Record<number, boolean>>>
  setRefreshFailedFlightKeys: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>
}

interface UseCartPanelActionsReturn {
  handleToggleInclude: (item: CartPanelItem) => Promise<void>
  handleSetDates: () => void
  handleChooseTickets: (item: CartPanelItem) => void
  handleChooseDate: (item: CartPanelItem) => void
  handleChooseTime: (item: CartPanelItem) => void
  handleChooseDifferentTickets: (item: CartPanelItem) => void
  handleChooseFlight: (item: CartPanelFlightItem) => Promise<void>
  handleRefreshFlight: (item: CartPanelFlightItem) => Promise<void>
  handleChangeFlight: (item: CartPanelFlightItem) => void
  handleViewFlightDetails: (item: CartPanelFlightItem) => void
  refreshingFlightKey: string | undefined
  isFlightCartMutationPending: boolean
  handleDelete: (item: CartPanelItem) => void
  handleCheckout: () => Promise<void>
  isCheckingOut: boolean
  flightSearchDrawer: React.ReactNode
}

function getExperienceContextDetails(item: CartPanelItem): string {
  if (item.productType !== 'experience') return ''
  const ticketSummary = item.tickets.length > 0
    ? item.tickets.map(ticket => `${ticket.count} x ${ticket.name}`).join(', ')
    : 'none'
  return `experience_id=${item.experienceId}, current_date=${item.date ?? 'none'}, current_time=${item.time ?? 'none'}, current_tickets=${ticketSummary}`
}

function getCartStatusPatchItemIds(item: CartPanelItem): Array<number> {
  return item.productType === 'flight' ? item.pairedTripItemIds : [item.tripItemId]
}

function setOptimisticCartExcludedForItemIds(
  setOptimisticExclusions: React.Dispatch<React.SetStateAction<Record<number, boolean>>>,
  itemIds: Array<number>,
  cartExcluded: boolean,
) {
  setOptimisticExclusions(prev => {
    const next = { ...prev }
    for (const itemId of itemIds) {
      next[itemId] = cartExcluded
    }
    return next
  })
}

export function useCartPanelActions({
  chatId,
  multiCartId,
  items,
  isStreaming,
  isLoggedIn,
  sendMessage,
  deleteManualItineraryTarget,
  setOptimisticExclusions,
  setRefreshFailedFlightKeys,
  refetchCartItems,
}: UseCartPanelActionsArgs): UseCartPanelActionsReturn {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { checkoutCartItems, prepareCheckoutCartItems, isCheckingOut } = useTripChatCheckoutActions({ chatId, multiCartId, refetchCartItems })

  const defaultErrorMessage = intl.formatMessage({
    id: 'EmZPR-KxU7oRFczGeJk2',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Default error message when a chat streaming request fails',
  })
  const { viewBookedFlightDetails, viewUnbookedFlightDetails } = useViewBookedShortlistedFlight(showModal)

  const handleViewFlightDetails = useCallback((item: CartPanelFlightItem) => {
    if (item.isBooked) {
      viewBookedFlightDetails(item.option)
    } else {
      viewUnbookedFlightDetails(item.option, undefined, undefined, item.flightCartItem)
    }
  }, [viewBookedFlightDetails, viewUnbookedFlightDetails])

  const flightCartActions = useTripChatFlightCartActions({
    chatId,
    multiCartId,
    isStreaming,
    isLoggedIn,
    setRefreshFailedFlightKeys,
  })

  useEffect(() => {
    if (isStreaming || !isLoggedIn) return
    prepareCheckoutCartItems(items)
  }, [isStreaming, isLoggedIn, items, prepareCheckoutCartItems])

  const handleToggleInclude = useCallback(async(item: CartPanelItem) => {
    if (!chatId) return
    const newValue = !item.cartExcluded
    const itemIds = getCartStatusPatchItemIds(item)
    setOptimisticCartExcludedForItemIds(setOptimisticExclusions, itemIds, newValue)
    try {
      await patchCartItemStatus(chatId, itemIds.map(itemId => ({ itemId, cartExcluded: newValue })))
    } catch {
      setOptimisticCartExcludedForItemIds(setOptimisticExclusions, itemIds, !newValue)
      showSnackbar(defaultErrorMessage, 'critical')
    }
  }, [chatId, setOptimisticExclusions, defaultErrorMessage])

  const handleSetDates = useCallback(() => {
    if (isStreaming) return
    const visible = intl.formatMessage({
      id: 'P3hBAGO4KmvHTWFNY4Ku',
      defaultMessage: 'Set dates for my trip',
      description: 'Visible user message when requesting to set trip dates from the cart panel',
    })
    const hidden = 'Set dates for the trip'
    sendMessage(appendContextTag(visible, hidden, TRIP_PLANNER_ACTION_CONTEXT_LABEL)).catch(() => {
      showSnackbar(defaultErrorMessage, 'critical')
    })
  }, [isStreaming, sendMessage, intl, defaultErrorMessage])

  const handleChooseTickets = useCallback((item: CartPanelItem) => {
    if (isStreaming || item.productType !== 'experience') return
    const visible = intl.formatMessage(
      {
        id: 'T3MXH7UtuKhS9YiiPz6x',
        defaultMessage: 'Choose tickets for {name}',
        description: 'Visible user message when choosing experience tickets from the cart panel',
      },
      { name: item.name },
    )
    const hidden = `Choose tickets for ${item.name} (item_id=${item.tripItemId}, ${getExperienceContextDetails(item)})`
    sendMessage(appendContextTag(visible, hidden, TRIP_PLANNER_ACTION_CONTEXT_LABEL)).catch(() => {
      showSnackbar(defaultErrorMessage, 'critical')
    })
  }, [isStreaming, sendMessage, intl, defaultErrorMessage])

  const handleChooseDate = useCallback((item: CartPanelItem) => {
    if (isStreaming || item.productType !== 'experience') return
    const visible = intl.formatMessage(
      {
        id: 'rfyOAs54FsORxOXJtJuj',
        defaultMessage: 'Choose date for {name}',
        description: 'Visible user message when choosing an experience date from the cart panel',
      },
      { name: item.name },
    )
    const hidden = `Choose date for ${item.name} (item_id=${item.tripItemId}, ${getExperienceContextDetails(item)})`
    sendMessage(appendContextTag(visible, hidden, TRIP_PLANNER_ACTION_CONTEXT_LABEL)).catch(() => {
      showSnackbar(defaultErrorMessage, 'critical')
    })
  }, [isStreaming, sendMessage, intl, defaultErrorMessage])

  const handleChooseTime = useCallback((item: CartPanelItem) => {
    if (isStreaming || item.productType !== 'experience') return
    const visible = intl.formatMessage(
      {
        id: 'TMVoV5HiBDryBxuj4vHm',
        defaultMessage: 'Choose time for {name}',
        description: 'Visible user message when choosing an experience time from the cart panel',
      },
      { name: item.name },
    )
    const hidden = `Choose time for ${item.name} (item_id=${item.tripItemId}, ${getExperienceContextDetails(item)})`
    sendMessage(appendContextTag(visible, hidden, TRIP_PLANNER_ACTION_CONTEXT_LABEL)).catch(() => {
      showSnackbar(defaultErrorMessage, 'critical')
    })
  }, [isStreaming, sendMessage, intl, defaultErrorMessage])

  const handleChooseDifferentTickets = useCallback((item: CartPanelItem) => {
    if (isStreaming || item.productType !== 'experience') return
    const visible = intl.formatMessage(
      {
        id: 'IdnwrjdyBiWbhs3TgLiJ',
        defaultMessage: 'Choose different tickets for {name}',
        description: 'Visible user message when selected experience tickets are unavailable',
      },
      { name: item.name },
    )
    const hidden = `Choose different tickets for ${item.name} (item_id=${item.tripItemId}, ${getExperienceContextDetails(item)}). Selected tickets are unavailable.`
    sendMessage(appendContextTag(visible, hidden, TRIP_PLANNER_ACTION_CONTEXT_LABEL)).catch(() => {
      showSnackbar(defaultErrorMessage, 'critical')
    })
  }, [isStreaming, sendMessage, intl, defaultErrorMessage])

  const handleDelete = useCallback(async(item: CartPanelItem) => {
    if (isStreaming) return
    const confirmed = await showModal<boolean>(
      <ConfirmDeleteCartItemModal
        itemName={item.name}
        isInCart={!!item.cartItemId}
      />,
    )
    if (!confirmed) return

    // A grouped multi-city itinerary is several trip items behind one tile —
    // deleting it removes every leg.
    const itemIds = item.productType === 'flight' && item.multiCityLegs
      ? item.multiCityLegs.map(leg => leg.tripItemId)
      : [item.tripItemId]
    for (const itemId of itemIds) {
      await deleteManualItineraryTarget({ targetType: 'item', itemId })
    }
  }, [isStreaming, showModal, deleteManualItineraryTarget])

  // Multi-city itineraries are presented as one grouped tile but are several
  // trip items underneath. These wrappers fan the per-leg flight actions out
  // across every leg (sequentially, so the cart mutations don't race), so a
  // single click converts / refreshes the whole itinerary.
  const handleChooseFlight = useCallback(async(item: CartPanelFlightItem) => {
    if (item.multiCityLegs) {
      for (const leg of item.multiCityLegs) {
        if (leg.cartStatus === 'needs_flight_selection') {
          const didChoose = await flightCartActions.handleChooseFlight(leg)
          if (!didChoose) return
        }
      }
      return
    }
    await flightCartActions.handleChooseFlight(item)
  }, [flightCartActions])

  const handleRefreshFlight = useCallback(async(item: CartPanelFlightItem) => {
    if (item.multiCityLegs) {
      for (const leg of item.multiCityLegs) {
        const didRefresh = await flightCartActions.handleRefreshFlight(leg)
        if (!didRefresh) return
      }
      return
    }
    await flightCartActions.handleRefreshFlight(item)
  }, [flightCartActions])

  const handleChangeFlight = useCallback((item: CartPanelFlightItem) => {
    // Re-selecting a multi-city trip re-opens the whole-trip search drawer (the
    // grouped item carries every leg for whole-itinerary replacement).
    flightCartActions.handleChangeFlight(item)
  }, [flightCartActions])

  const handleCheckout = useCallback(async() => {
    rememberTripChatCheckoutReturnView('cart')
    await checkoutCartItems(items)
  }, [checkoutCartItems, items])

  return {
    handleToggleInclude,
    handleSetDates,
    handleChooseTickets,
    handleChooseDate,
    handleChooseTime,
    handleChooseDifferentTickets,
    handleChooseFlight,
    handleRefreshFlight,
    handleChangeFlight,
    handleViewFlightDetails,
    refreshingFlightKey: flightCartActions.refreshingFlightKey,
    isFlightCartMutationPending: flightCartActions.isFlightCartMutationPending,
    handleDelete,
    handleCheckout,
    isCheckingOut,
    flightSearchDrawer: flightCartActions.flightSearchDrawer,
  }
}
